// src/adminPanels/index.js — the wave-2 FINALE grab-bag (moved VERBATIM from
// admin.html in twelve ranges, FE-breakup slice 8; bundled to dist/adminPanels.js):
// dashboard renders, opening-hours season editor, clock-log CSV/AI tools,
// store-admin modals, Amanah template download, AI-rota form helpers
// (buildDayTogglesIn/addShiftTemplateToForm/safeTimeInputValue — called by
// Uploads.rules + Uploads.stores), dev-tab audit/error tools, the app-status
// editor, and area-KPI controls.
//
// PAGE-GLOBAL STATE CONTRACT (all declarations stay in admin.html):
// _hoursData/_hoursModal*/_hoursEditingSeason/HOURS_DAYS (written by
// Uploads.stores), clockEditing* (written by Uploads.clockReview),
// _devReports/_devExpanded/_errorReports/_errorsExpanded/_triageInFlight/
// _triageResults (written by Uploads.devReports — which documents "State
// lives in admin.html"), _appStatus*, _areaKpi* (v104-1 pins _areaKpiData's
// declaration), staffData/ALL_AREAS/ALL_STORES. The store LOOKUP helpers
// (getStoreMeta/Type/Area/Country/UploadName, buildUploadNameMap,
// VIRTUAL_STORES — v104-1 pins its position) and DAYS_LIST stay inline as
// frontend CORE: 7+ bundles call them.
// downloadAmanahTemplate's window binding travelled with its range.

import { askSecret } from '../shared/ask-secret.js';   // v133.120: passwords never in a prompt() box
import '../shared/confirm-dialog.js';   // v134.2: sets window.uiConfirm for the admin app
import '../shared/table-cards.js';   // v134.3: admin tables become cards on a phone
import { formatCoords } from '../shared/store-coords.js';   // v131.72 - one definition of a valid coordinate

function renderDashboardAreaStats(stats) {
  const container = document.getElementById('dashboardAreaStats');
  if (!container) return;
  const html = ALL_AREAS.map(a => {
    const n = (stats && stats[a.id]) || 0;
    const label = a.country ? a.name + ' (' + a.country + ')' : a.name;
    return '<div class="stat-card"><div class="stat-num">' + n +
           '</div><div class="stat-label" translate="no">' + escapeHtml(label) + '</div></div>';   // v135.2: an area name is data, never translated
  }).join('');
  container.innerHTML = html;
}

// ── STAFF DATA — loaded from database ─────────────────────────────────────

function downloadAmanahTemplate() {
  var headers = ['First Name','Last Name','Emp Code','Division','Department','Designation','Retail Store'];
  var example = ['Jane','Doe','EMP12345','Retail','Operations','Sales Associate','Dubai - Dubai Hills'];
  function cell(v){ v = String(v == null ? '' : v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g,'""') + '"' : v; }
  var csv = [headers, example].map(function(r){ return r.map(cell).join(','); }).join('\r\n');
  var blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  var a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'ELC_Amanah_Import_Template.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}
window.downloadAmanahTemplate = downloadAmanahTemplate;

function summarizeHoursRow(row) {
  // Build [{day, open, close}, ...] in week order
  var entries = HOURS_DAYS.map(function(d){
    return {
      day:   d.label.slice(0,3),
      open:  row[d.key+'_open']  || '',
      close: row[d.key+'_close'] || '',
    };
  });

  // Group consecutive same-pattern days
  var groups = [];
  var cur = null;
  entries.forEach(function(e){
    var sig = e.open + '|' + e.close;
    if (cur && cur.sig === sig) {
      cur.days.push(e.day);
    } else {
      cur = { sig: sig, open: e.open, close: e.close, days: [e.day] };
      groups.push(cur);
    }
  });

  return groups.map(function(g){
    var range = g.days.length === 1 ? g.days[0] : g.days[0] + '–' + g.days[g.days.length-1];
    if (!g.open && !g.close) return '<span style="color:var(--sub)">Closed '+range+'</span>';
    return escapeHtml((g.open||'?') + '–' + (g.close||'?')) + ' <span style="color:var(--sub)">'+range+'</span>';
  }).join(' · ');
}

function switchHoursSeason(season) {
  if (season !== 'regular' && season !== 'ramadan') return;
  _hoursEditingSeason = season;
  // Update tab visual state
  var regBtn = document.getElementById('hoursSeasonRegularBtn');
  var ramBtn = document.getElementById('hoursSeasonRamadanBtn');
  if (regBtn && ramBtn) {
    var activeStyle = 'background:var(--green-on);color:#fff';   // v136.8: the green white text reads on (5.5:1)
    var inactiveStyle = 'background:var(--card);color:var(--text)';
    var active = season === 'regular' ? regBtn : ramBtn;
    var inactive = season === 'regular' ? ramBtn : regBtn;
    active.style.background = 'var(--green-on)';
    active.style.color = '#fff';
    inactive.style.background = 'var(--card)';
    inactive.style.color = 'var(--text)';
  }
  Uploads.stores.renderStoreHours();
}

async function loadCurrentSeason() {
  try {
    var val = await DB.config.get('current_season');
    if (val && (val.value === 'regular' || val.value === 'ramadan')) {
      _hoursCurrentSeason = val.value;
      updateCurrentSeasonBadge();
    }
  } catch (e) {
    // Non-fatal — badge stays on last known value.
  }
}

function updateCurrentSeasonBadge() {
  var badge = document.getElementById('currentSeasonBadge');
  if (!badge) return;
  if (_hoursCurrentSeason === 'ramadan') {
    badge.textContent = 'Ramadan';
    badge.style.background = 'var(--warn-bg)';
    badge.style.color = 'var(--warn-deep)';
  } else {
    badge.textContent = 'Regular';
    badge.style.background = 'var(--ok-bg)';
    badge.style.color = 'var(--ok)';
  }
}

// How many active stores have no hours for the season being flipped TO, and
// which. Pure so it can be tested; the caller supplies the rows and the store
// list. Found 1 Oct 2026: only 12 of the 29 stores have Ramadan hours, because
// the v82 vendor spreadsheet carried Ramadan rows for 12 sites only. Flipping
// the season drops the other 17 onto the DEFAULT times, which is the one moment
// somebody can still choose not to.
function seasonFlipWarning(rows, stores, season) {
  var missing = (stores || []).filter(function (name) {
    return !(rows || []).some(function (r) { return r && r.store === name && r.season === season; });
  });
  if (!missing.length) return '';
  var shown = missing.slice(0, 8).join(', ');
  var rest = missing.length > 8 ? ' and ' + (missing.length - 8) + ' more' : '';
  return '\n\nWARNING: ' + missing.length + ' of ' + stores.length + ' stores have NO ' + season
    + ' hours, so they will fall back to the DEFAULT opening times until somebody enters them:\n'
    + shown + rest + '\n\nThe ' + season.charAt(0).toUpperCase() + season.slice(1)
    + ' tab on this screen lists them in red.';
}

async function toggleCurrentSeason() {
  var next = _hoursCurrentSeason === 'regular' ? 'ramadan' : 'regular';
  // Read the hours fresh rather than trusting _hoursData, which is only
  // populated once the Hours tab has been opened: the flip button is reachable
  // without that, and a silent empty list would under-report the gap as zero.
  var warn = '';
  try {
    var rows = await DB.openingHours.list();
    var stores = (typeof ALL_STORES !== 'undefined' ? ALL_STORES : []).filter(function (x) { return x !== 'Retail Relief Managers'; });
    warn = seasonFlipWarning(rows, stores, next);
  } catch (e) {
    // Never block the flip on a failed read, but never imply it is safe either.
    warn = '\n\nNOTE: could not check which stores have ' + next + ' hours (' + (e && e.message) + '), so this flip may put some stores on the default times.';
  }
  if (!confirm('Flip active season to '+next.toUpperCase()+'?\n\nThe rota generator will use the '+next+' hours immediately after this changes. Already-generated rotas are not retroactively recalculated.'+warn)) {
    return;
  }
  try {
    await DB.config.set('current_season', next);
    _hoursCurrentSeason = next;
    updateCurrentSeasonBadge();
    showToast('Active season → '+next, 'success');
  } catch (e) {
    showToast('Failed to flip season: '+(e && e.message), 'error');
  }
}

// v104.0: openStoreHoursModal → Uploads.stores.openStoreHoursModal
// v104.0: closeStoreHoursModal → Uploads.stores.closeStoreHoursModal
// Copy one day's open/close to a range of other days. Used by the modal's
// header helper buttons: "Mon → Tue-Thu" (weekday copy), "Fri → Sat"
// (weekend copy), "Mon → all days" (uniform week).
function copyHoursDayToRange(srcDay, rangeName) {
  var ranges = {
    'weekdays': ['tue','wed','thu'],
    'weekend':  ['sat'],
    'all':      HOURS_DAYS.map(function(d){return d.key;}).filter(function(d){return d !== srcDay;}),
  };
  var targets = ranges[rangeName] || [];
  var srcOpen  = document.getElementById('hoursInput_'+srcDay+'_open').value;
  var srcClose = document.getElementById('hoursInput_'+srcDay+'_close').value;
  targets.forEach(function(d){
    var openEl  = document.getElementById('hoursInput_'+d+'_open');
    var closeEl = document.getElementById('hoursInput_'+d+'_close');
    if (openEl)  openEl.value  = srcOpen;
    if (closeEl) closeEl.value = srcClose;
  });
}

// v104.0: saveStoreHoursFromModal → Uploads.stores.saveStoreHoursFromModal
// v104.0: saveStoreHour → Uploads.stores.saveStoreHour

// ── DASHBOARD ─────────────────────────────────────────────────────────────
function renderDashboard() {
  const visible = getVisibleStaff();
  document.getElementById('dTotalStaff').textContent = visible.length;
  document.getElementById('dStores').textContent = new Set(visible.map(s => s.store)).size;
  document.getElementById('dPendingReqs').textContent = requestsData.filter(r => r.status === 'pending').length;
  // v133.122: this tile was never filled, so it showed the page's hard-coded "3" forever. The real count of
  // active area manager accounts; a dash if the accounts list has not loaded.
  try {
    const accs = (typeof loadAccounts === 'function') ? (loadAccounts() || []) : [];
    document.getElementById('dManagers').textContent = accs.length
      ? accs.filter(a => a && a.role === 'area_manager' && a.active !== false).length : '–';
  } catch (e) { /* keep the dash */ }
  ['dTotalStaff', 'dStores', 'dPendingReqs', 'dManagers'].forEach(id => { const el = document.getElementById(id); if (el) el.removeAttribute('aria-busy'); });
  // Per-area staff counts — render the whole row dynamically from ALL_AREAS
  // so staging shows staging areas and production shows production. Replaces
  // a previous version that hardcoded the area-stat element ids by name.
  const areaStats = {};
  ALL_AREAS.forEach(a => {
    areaStats[a.id] = staffData.filter(s => s.area === a.id && s.active !== false).length;
  });
  renderDashboardAreaStats(areaStats);
  updateChangesBadge();
  renderDashboardCustomerSat();   // ⭐ Customer Satisfaction KPI tile (fire-and-forget)
  scheduleDashboardBrief();   // v134.5: the daily brief, after this paint (never blocks the tiles)

}

// ── v134.5: the DAILY BRIEF on the home page ────────────────────────────────
// The same brief the owner gets by email each morning (GET /api/admin/business-brief; Head Office sees the
// company, an area manager their own stores). Fetched ONCE per page load, after the tiles have painted:
// renderDashboard runs again on every request or change saved, and the brief is a heavy read that does not
// change minute to minute. A failure says what went wrong in the card and leaves the rest of the page alone.
// Head Office and area managers only (the route refuses anyone else); a store manager's home stays as it was.
let _dashBriefStarted = false;
function dashBriefRoleAllowed() {
  const a = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
  const r = String((a && a.role) || '').toLowerCase();
  return r === 'admin' || r === 'area_manager';
}
// v134.8 (seen live on staging, 15 Oct 2026): the dashboard first draws BEFORE the sign-in has set currentAdmin,
// and nothing redraws it afterwards, so the brief never started on its own. Until the account is known, look
// again every half second for up to 30 seconds.
let _dashBriefWaits = 0;
function scheduleDashboardBrief() {
  if (_dashBriefStarted) return;
  if (!dashBriefRoleAllowed()) {
    const a = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
    if (!(a && a.role) && _dashBriefWaits++ < 60) setTimeout(scheduleDashboardBrief, 500);   // not signed in yet
    return;
  }
  _dashBriefStarted = true;
  const later = function () { setTimeout(renderDashboardBrief, 0); };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(later); else later();
}

function dashBriefSkeleton() {
  const bar = function (w, h) { return '<span class="ui-skel" style="width:' + w + ';height:' + (h || 14) + 'px;margin:0 0 10px"></span>'; };
  return '<div aria-busy="true" aria-label="Loading the daily brief">'
    + bar('55%', 18) + bar('100%', 64) + bar('92%') + bar('80%') + bar('86%') + bar('64%') + '</div>';
}

function dashBriefDate(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

async function renderDashboardBrief() {
  const card = document.getElementById('dashBriefCard');
  const body = document.getElementById('dashBriefBody');
  const when = document.getElementById('dashBriefDate');
  if (!card || !body) return;
  const esc = (typeof escapeHtml === 'function') ? escapeHtml : function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); };
  card.style.display = '';
  body.innerHTML = dashBriefSkeleton();
  try {
    const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    const headers = {};
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const res = await fetch(base + '/api/admin/business-brief', { cache: 'no-store', headers: headers });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok || !data || !data.ok || typeof data.html !== 'string') {
      throw new Error((data && data.error) || ('the server answered ' + res.status));
    }
    // v135.2: each word in its own span, so the Arabic switch can swap the day and month names (the catalogue has them)
    if (when) when.innerHTML = String(dashBriefDate(data.day)).replace(/,/g, '').split(' ').filter(Boolean).map(function (w) { return '<span>' + esc(w) + '</span>'; }).join(' ')
      + (data.scope === 'area' ? ' <span>· your area</span>' : '');
    // The email's own HTML (composed and escaped server-side), on a white sheet so its colours read as they do
    // in the inbox. v136 dark mode: the sheet stays white in Dark too, deliberately (the HTML is the email's, with its
    // own light colours, and the Worker is shared with the email so it is left alone); dark text and a light colour
    // scheme are pinned here so nothing it leaves unset inherits the dark page's light text.
    body.innerHTML = '<div style="background:#fff;color:#111b21;color-scheme:light;border:1px solid var(--border);border-radius:12px;overflow-x:auto">' + data.html + '</div>';
  } catch (e) {
    body.innerHTML = '<div role="alert" style="font-size:13px;color:var(--adm-red-text);padding:4px 2px">'
      + 'Could not load the daily brief: ' + esc((e && e.message) || 'unknown error') + '.'
      + ' <button class="btn btn-outline btn-sm" style="margin-inline-start:6px" onclick="renderDashboardBrief()">Try again</button></div>';
  }
}

// ⭐ Customer Satisfaction KPI on the dashboard — reuses the scorecard read
// (/api/admin/customer-feedback; HO sees all, an area manager sees their area on
// the 1-hour delay). Hidden until there's data; full breakdown is in the ⭐ Customer tab.
// v136.8 (the 16 Oct render audit): renderDashboard() runs from nine places (every staff edit, request,
// approval...) and each one re-read the whole customer-feedback scorecard. The answer is now kept for
// CUST_SAT_TTL_MS per page load and per sign-in (the token): a repeat draw inside that writes the kept
// result, or nothing at all when it is already on the card. A failed read is not kept (the next draw asks).
var CUST_SAT_TTL_MS = 5 * 60 * 1000;
var _custSat = null;   // { at, token, html } (html null = the card stays hidden: no reviews yet)
function _custSatApply(card, body, kept) {
  if (kept.html == null) { card.style.display = 'none'; return; }
  if (body.getAttribute('data-cs-at') !== String(kept.at)) {   // not already on the card
    body.innerHTML = kept.html;
    body.setAttribute('data-cs-at', String(kept.at));
  }
  card.style.display = '';
}
async function renderDashboardCustomerSat() {
  var card = document.getElementById('custSatCard');
  var body = document.getElementById('custSatBody');
  if (!card || !body) return;
  try {
    var token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
    if (_custSat && _custSat.token === token && (Date.now() - _custSat.at) < CUST_SAT_TTL_MS) {
      _custSatApply(card, body, _custSat);
      return;
    }
    var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    var headers = {};
    if (token) headers['Authorization'] = 'Bearer ' + token;
    var res = await fetch(base + '/api/admin/customer-feedback', { cache: 'no-store', headers: headers });
    if (!res.ok) { card.style.display = 'none'; return; }
    var data = await res.json();
    var o = (data.scorecard && data.scorecard.overall) || {};
    if (!o.count) { _custSat = { at: Date.now(), token: token, html: null }; card.style.display = 'none'; return; }   // no reviews yet → hide the card
    var byStore = (data.scorecard && data.scorecard.byStore) || [];
    var worst = byStore.length ? byStore[0] : null;          // byStore is sorted worst-NPS first
    var ratingC = o.avg >= 8 ? '#059669' : o.avg >= 6 ? '#D97706' : '#DC2626';
    var npsC = o.nps >= 50 ? '#059669' : o.nps >= 0 ? '#D97706' : '#DC2626';
    var html =
      '<div style="display:flex;gap:12px;flex-wrap:wrap">' +
        custSatTile('Avg rating', (o.avg == null ? '—' : o.avg) + '<span style="font-size:14px;color:var(--tiny)">/10</span>', o.count + ' response' + (o.count === 1 ? '' : 's'), ratingC) +
        custSatTile('NPS', (o.nps == null ? '—' : (o.nps > 0 ? '+' : '') + o.nps), o.promoters + ' promoters · ' + o.detractors + ' detractors', npsC) +
        (worst ? custSatTile('Needs attention', escapeHtml(worst.store), 'NPS ' + (worst.nps == null ? '—' : worst.nps) + ' · ' + worst.count + ' review' + (worst.count === 1 ? '' : 's'), npsC) : '') +
      '</div>' +
      '<div style="font-size:11px;color:var(--sub);margin-top:10px">Full breakdown + comments in the ⭐ Customer tab.' + (data.scoped ? ' (your area · 1-hour delayed)' : '') + '</div>';
    _custSat = { at: Date.now(), token: token, html: html };
    _custSatApply(card, body, _custSat);
  } catch (e) { card.style.display = 'none'; }
}
function custSatTile(label, value, sub, color) {
  return '<div style="flex:1;min-width:130px;background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px;text-align:center">' +
    '<div style="font-size:28px;font-weight:900;line-height:1;color:' + color + '">' + value + '</div>' +
    '<div style="font-size:12px;font-weight:700;margin-top:6px">' + label + '</div>' +
    (sub ? '<div style="font-size:11px;color:var(--sub);margin-top:2px">' + sub + '</div>' : '') + '</div>';
}

function safeTimeInputValue(v){
  return v === '24:00' ? '23:59' : (v || '');
}

function downloadClockLogCSV() {
  const rows = window._clockLogCSV || [];
  if (!rows.length) { showToast('No data — run a filter first', 'error'); return; }
  const csv = ['Name,Store,Date,Clock In,Clock Out,Break (min),Scheduled,Variance,Amended'];
  rows.forEach(r => csv.push([`"${r.name}"`,`"${r.store}"`,r.date,r.clockIn,r.clockOut,r.breakMins||0,r.scheduled,r.variance,r.amended].join(',')));
  const blob = new Blob([csv.join('\n')], {type:'text/csv'});
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = 'ELC_ClockLog_'+todayDateStr()+'.csv'; a.click();
  showToast('Clock log downloaded', 'success');
}

async function runClockLogAI() {
  const resultDiv = document.getElementById('clockLogAiResult');
  const textDiv = document.getElementById('clockLogAiText');
  if (!resultDiv || !textDiv) return;   // v136.8: null-safe (the box may not be on screen)
  resultDiv.style.display = 'block';
  textDiv.innerHTML = '<span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span> Analysing clock data...';

  const rows = window._clockLogCSV || [];
  if (!rows.length) { textDiv.innerHTML = 'No data — apply a filter first.'; return; }

  // Summarise patterns
  const late = rows.filter(r => r.variance.includes('late'));
  const early = rows.filter(r => r.variance.includes('early'));
  const missing = rows.filter(r => r.clockOut === '—');
  const amended = rows.filter(r => r.amended === 'Yes');

  // Per person late count
  const lateCounts = {};
  late.forEach(r => { lateCounts[r.name] = (lateCounts[r.name]||0) + 1; });
  const habituallyLate = Object.entries(lateCounts).filter(([,c]) => c >= 2).map(([n,c]) => `${n} (${c}x late)`);

  const summary = `Total records: ${rows.length}
Late arrivals: ${late.length} (${Math.round(late.length/rows.length*100)}%)
Early leavers: ${early.length}
Missing clock-out: ${missing.length}
Amended records: ${amended.length}
Habitually late (2+ times): ${habituallyLate.join(', ') || 'None'}
Period: ${rows[rows.length-1]?.date} to ${rows[0]?.date}`;

  const prompt = `You are reviewing clock-in/out data for ELC cosmetics retail stores.\n\n${summary}\n\nSample late arrivals: ${late.slice(0,5).map(r=>r.name+' '+r.date+' '+r.variance).join('; ')}\nMissing clock-outs: ${missing.slice(0,5).map(r=>r.name+' '+r.date).join('; ')}\n\nPlease provide:\n1. Staff who are consistently late or have attendance concerns\n2. Stores with higher than expected absence/issues\n3. Any patterns worth flagging to management\n4. Recommended actions for the top 3 issues\nBe concise and direct. Use bullet points.`;

  try {
    const data = await DB.ai.rota({model:'claude-haiku-4-5-20251001',max_tokens:800,
        system:'You are a retail HR analyst. Be concise, use bullet points, prioritise urgent issues.',
        messages:[{role:'user',content:prompt}]});
    const text = (data.content?.[0]?.text)||'';
    textDiv.innerHTML = escapeHtml(text).replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>').replace(/^[•\-\*] /gm,'<br>• ').replace(/\n/g,'<br>');   // v136.8 (M3): model text escaped before formatting
  } catch(err) { textDiv.innerHTML = '❌ ' + escapeHtml(err.message); }
}

// v102.0: openClockEditModal → Uploads.clockReview.openClockEditModal
// v102.0: closeClockEditModal → Uploads.clockReview.closeClockEditModal
// v102.0: saveClockEdit → Uploads.clockReview.saveClockEdit
// ── STORE MANAGEMENT ──────────────────────────────────────────────────────

// Updates the Area Manager dropdown when country changes in store modal
function updateStoreAreaOptions() {
  const country = document.getElementById('smStoreCountry')?.value || '';
  const areaSel = document.getElementById('smStoreArea');
  if (!areaSel) return;
  const isQatar = country === 'ELC - Qatar';
  const isUAE = country === 'ELC - UAE';
  // v60: re-populate options based on country, derived from ALL_AREAS rather
  // than hardcoded. Country match is a string contains because ALL_AREAS rows
  // store country as e.g. "UAE", "Dubai", "Qatar" (not "ELC - UAE").
  areaSel.innerHTML = '';
  let filtered;
  if (isUAE) {
    filtered = ALL_AREAS.filter(a => /uae|dubai|abu dhabi|sharjah|ajman/i.test(a.country || ''));
  } else if (isQatar) {
    filtered = ALL_AREAS.filter(a => /qatar|doha/i.test(a.country || ''));
  } else {
    filtered = ALL_AREAS.slice();
  }
  filtered.forEach(a => {
    const el = document.createElement('option');
    el.value = a.id;
    el.textContent = a.country ? a.name + ' (' + a.country + ')' : a.name;
    areaSel.appendChild(el);
  });
  // Always offer the "none / virtual" sentinel
  const noneOpt = document.createElement('option');
  noneOpt.value = ''; noneOpt.textContent = 'None / Virtual';
  areaSel.appendChild(noneOpt);
}

// v104.0: downloadTargetsTemplate → Uploads.budget.downloadTargetsTemplate
// v79: Sales template — symmetrical with the Budget template above. Generates
// a 3-sheet xlsx file matching the structure of the ALGT_Store_Target_Sheet
// master file. User fills in their daily sales + last-year numbers, uploads
// to the Upload Sales tab. Sheet names MUST match what parseSalesWorkbook()
// looks for, otherwise the upload silently fails to find the sheets.
// v92.0: downloadSalesTemplate removed — legacy Sales tab deleted

function openAddStoreModal() {
  document.getElementById('storeModalTitle').textContent = 'Add Store';
  document.getElementById('smStoreName').value = '';
  document.getElementById('smUploadName').value = '';
  document.getElementById('smStoreArea').value = getDefaultAreaId();
  document.getElementById('smStoreCountry').value = 'ELC - UAE';
  document.getElementById('smStoreType').value = 'physical';
  document.getElementById('smStoreOldName').value = '';
  const smEmailAdd = document.getElementById('smStoreEmail'); if (smEmailAdd) smEmailAdd.value = '';   // v125.53
  const smGoogleAdd = document.getElementById('smStoreGoogleUrl'); if (smGoogleAdd) smGoogleAdd.value = '';   // v131.64
  const smCoordsAdd = document.getElementById('smStoreCoords'); if (smCoordsAdd) smCoordsAdd.value = '';   // v131.72
  document.getElementById('storeModal').classList.add('open');
}

function openEditStoreModal(name) {
  document.getElementById('storeModalTitle').textContent = 'Edit Store';
  document.getElementById('smStoreName').value = name;
  // Show existing upload name if it differs from store name, else leave blank
  const uploadName = getStoreUploadName(name);
  document.getElementById('smUploadName').value = uploadName === name ? '' : uploadName;
  document.getElementById('smStoreArea').value = getStoreArea(name) || getDefaultAreaId();
  document.getElementById('smStoreCountry').value = getStoreCountry(name);
  document.getElementById('smStoreType').value = getStoreType(name);
  document.getElementById('smStoreOldName').value = name;
  // v125.53 — pre-fill the store's current email from the cached map.
  const smEmail = document.getElementById('smStoreEmail');
  if (smEmail) smEmail.value = (window._storeEmailMap && window._storeEmailMap[name]) || '';
  // v131.64 — pre-fill the Google review link from the cached store rows, so
  // editing a store shows what is already set rather than an empty box.
  const smGoogle = document.getElementById('smStoreGoogleUrl');
  if (smGoogle) smGoogle.value = (window._storeGoogleMap && window._storeGoogleMap[name]) || '';
  // v131.72 - the captured shop coordinate, same cached-map pattern.
  const smCoords = document.getElementById('smStoreCoords');
  if (smCoords) {
    const c = (window._storeCoordMap && window._storeCoordMap[name]) || null;
    smCoords.value = c ? formatCoords(c.lat, c.lng) : '';
  }
  document.getElementById('storeModal').classList.add('open');
}

// v104.0: closeStoreModal → Uploads.stores.closeStoreModal
// v104.0: saveStoreModal → Uploads.stores.saveStoreModal
// v104.0: deleteStore → Uploads.stores.deleteStore
async function changeStorePassword(store) {
  // v133.120: a Shift pop-up that hides what is typed (the browser's prompt() box cannot)
  const newPw = await askSecret({ title: 'Device password for ' + store, note: 'Staff enter this once on their device to unlock the app.\nLeave it empty and press Save to remove the password.', allowEmpty: true });
  if (newPw === null) return; // cancelled
  try {
    await DB.auth.setStorePassword(store, newPw || '');
    if (newPw) {
      showToast('Password set for ' + store, 'success');
    } else {
      showToast('Password removed for ' + store, 'success');
    }
    // v131.37: the table's "Password" column shows the new stamp straight away.
    try {
      if (window.Uploads && Uploads.stores && Uploads.stores.loadStoresFromDB) {
        await Uploads.stores.loadStoresFromDB();
        if (Uploads.stores.renderStoreManagement) Uploads.stores.renderStoreManagement();
      }
    } catch (e) { /* cosmetic */ }
  } catch(e) {
    showToast('Failed: ' + (e.message || 'unknown error'), 'error');
  }
}

// v136.9 (Matt, 17 Oct 2026): a store's password login locks after 10 wrong tries in 15 minutes
// (a new password also clears it). Head Office can end the lock early from the Stores table; the
// Worker records it in auth_events and the audit log, and refuses anyone else.
async function unlockStoreLogin(store) {
  if (!store) return;
  try {
    const r = await DB.auth.unlockLogin({ store: store });
    showToast(r && r.was_locked ? 'Unlocked. They can sign in again now.' : 'That sign-in was not locked.', 'success');
  } catch (e) {
    showToast('Unlock failed: ' + ((e && e.message) || 'unknown error'), 'error');
  }
}

// ── HELPERS ───────────────────────────────────────────────────────────────

function buildDayTogglesIn(id, activeList, container) {
  const el = container ? container.querySelector('#' + id) : document.getElementById(id);
  if (!el) return;
  el.innerHTML = '';
  DAYS_LIST.forEach(day => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'day-toggle' + (activeList.includes(day) ? ' active' : '');
    btn.dataset.day = day;
    btn.textContent = day.slice(0,3);
    btn.onclick = function() { this.classList.toggle('active'); Uploads.rules.renderRulesPreview(); };
    el.appendChild(btn);
  });
}

function addShiftTemplateToForm() {
  const container = document.getElementById('r_shiftTemplates');
  if (!container) return;
  const row = document.createElement('div');
  row.className = 'shift-template-row';
  row.style.cssText = 'display:flex;gap:8px;align-items:center;margin-bottom:8px;flex-wrap:wrap';
  row.innerHTML = `<input class="form-input st-label" value="New" placeholder="Label" style="width:90px">
    <input type="time" class="form-input st-start" value="10:00" style="width:100px">
    <span style="color:var(--sub);font-size:12px">to</span>
    <input type="time" class="form-input st-finish" value="19:00" style="width:100px">
    <select class="form-input st-type" style="width:100px">
      <option value="early">Early</option><option value="mid">Mid</option><option value="late">Late</option>
    </select>
    <button onclick="this.closest('.shift-template-row').remove()" style="background:none;border:none;color:var(--red);cursor:pointer;font-size:16px">✕</button>`;
  container.appendChild(row);
}

// v79.15.1 — populate the write-audit store filter dropdown.
// ALL_STORES is an array of name strings (see line 2465 fallback +
// loadStoresFromDB which maps DB rows to .name strings). We sort
// alphabetically because the upstream order isn't guaranteed.
function populateWriteAuditStoreFilter() {
  const sel = document.getElementById('writeAuditStoreFilter');
  if (!sel || sel.children.length > 1) return;  // already populated
  if (typeof ALL_STORES === 'undefined' || !ALL_STORES || !ALL_STORES.length) return;
  const sorted = ALL_STORES.slice().sort();
  sorted.forEach(function(name){
    if (!name) return;
    const opt = document.createElement('option');
    opt.value = name;
    opt.textContent = name;
    sel.appendChild(opt);
  });
}

// v79.15.1 — load and render the write-audit log.
// v102.0: loadDevWriteAudit → Uploads.devReports.loadDevWriteAudit
// v60: load and render PIN reveal audit log. Most recent first.
// v102.0: loadDevReveals → Uploads.devReports.loadDevReveals
// ─────────────────────────────────────────────────────────────
// v59: STATUS PANEL — environment + versions + security + consent uptake
// ─────────────────────────────────────────────────────────────
// v102.0: renderDevStatus → Uploads.devReports.renderDevStatus
// v59: reset all (or per-store) privacy-notice acknowledgements. Forces every
// affected staff to re-prompt on next clock-in. Used when the notice text
// changes mid-cycle, or for testing the consent flow.
async function resetAllConsent() {
  if (!confirm('Reset privacy-notice acknowledgements for ALL active staff? Every staff will be asked to acknowledge again on their next clock-in. This cannot be undone.')) return;
  try {
    const res = await DB.staff.resetAllConsent();
    const n = (res && res.reset_count) || 0;
    showToast('Reset acknowledgements for ' + n + ' staff', 'success');
    // Refresh staff data so the Status panel + Directory dots update
    await DB.staff.list().then(rows => {
      // Update staffData in place — preserve any local fields not in the response
      (rows || []).forEach(r => {
        const local = staffData.find(x => x.id === r.id);
        if (local) {
          local.consent_version = r.consent_version;
          local.consent_acknowledged_at = r.consent_acknowledged_at;
          local.consent_lang = r.consent_lang;
        }
      });
    }).catch(()=>{});
    // v127.5: renderDevStatus refresh removed — the Status panel was retired.
    // v127.13: new visible home = the 🔐 button on the People→Staff sub-tab bar.
    Uploads.staff.renderStaff();  // v101.0: typeof guard removed — direct call after staff CRUD moved to module
  } catch(e) {
    console.error('[reset-consent]', e);
    showToast('Failed to reset acknowledgements: ' + (e.message || 'unknown'), 'error');
  }
}

// ─────────────────────────────────────────────────────────────
// v59: AUDIT PANEL — wraps existing Uploads.rota.auditShifts() in a UI
// ─────────────────────────────────────────────────────────────
function initDevAuditFilters() {
  const wkSel = document.getElementById('devAuditWeek');
  const stSel = document.getElementById('devAuditStore');
  if (!wkSel || !stSel) return;
  // Build week dropdown — last 8 weeks
  if (!wkSel.options.length) {
    const today = new Date();
    const monday = new Date(today);
    const day = monday.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    monday.setDate(monday.getDate() + diff);
    const opts = [];
    for (let i = 0; i < 8; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() - 7 * i);
      const ws = localDateStr(d);
      opts.push('<option value="' + ws + '"' + (i === 0 ? ' selected' : '') + '>Week of ' + ws + (i === 0 ? ' (current)' : '') + '</option>');
    }
    wkSel.innerHTML = opts.join('');
  }
  // Populate stores from staffData
  if (stSel.options.length <= 1) {
    const stores = Array.from(new Set(staffData.filter(s => s.active !== false).map(s => s.store))).filter(Boolean).sort();
    stSel.innerHTML = '<option value="">All stores</option>' + stores.map(st => '<option value="' + st + '">' + st + '</option>').join('');
  }
  Uploads.devReports.loadDevAudit();
}

// v102.0: loadDevAudit → Uploads.devReports.loadDevAudit
// v102.0: renderDevAudit → Uploads.devReports.renderDevAudit
// v104.0: loadErrorReports → Uploads.devReports.loadErrorReports
function updateErrorBadge() {
  const badge = document.getElementById('devErrorBadge');
  if (!badge) return;
  const newCount = _errorReports.filter(r => r.status === 'new').length;
  if (newCount > 0) {
    badge.style.display = '';
    badge.textContent = newCount;
  } else {
    badge.style.display = 'none';
  }
}

// v104.0: renderErrorReports → Uploads.devReports.renderErrorReports
// v104.0: renderErrorGroup → Uploads.devReports.renderErrorGroup
// v104.0: renderErrorRow → Uploads.devReports.renderErrorRow
// v104.0: renderErrorActions → Uploads.devReports.renderErrorActions
// v104.0: toggleErrorGroup → Uploads.devReports.toggleErrorGroup
// v104.0: toggleErrorRow → Uploads.devReports.toggleErrorRow
async function setErrorStatus(id, newStatus) {
  try {
    const result = await DB.errors.update(id, { status: newStatus });
    const updated = Array.isArray(result) ? result[0] : result;
    const idx = _errorReports.findIndex(r => String(r.id) === String(id));
    if (idx !== -1 && updated) _errorReports[idx] = updated;
    updateErrorBadge();
    Uploads.devReports.renderErrorReports();
    showToast('Status: ' + newStatus, 'success');
  } catch (e) {
    showToast('Update failed: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
}

// v104.0: saveErrorNotes → Uploads.devReports.saveErrorNotes

async function loadAppStatus() {
  if (typeof DB === 'undefined' || !DB.appStatus) return;
  try {
    const row = await DB.appStatus.get();
    _appStatusCurrent = (row && row.status) || 'green';
    _appStatusMessage = (row && row.message) || null;
    renderAppStatusPill();
  } catch(e) {
    console.warn('[AppStatus] load failed:', e && e.message);
  }
}

function renderAppStatusPill() {
  const pill = document.getElementById('appStatusPill');
  const label = document.getElementById('appStatusLabel');
  if (!pill || !label) return;
  pill.style.display = 'inline-flex';
  const colorMap = { green:'#10b981', amber:'#f59e0b', red:'#ef4444' };
  pill.style.background = colorMap[_appStatusCurrent] || '#10b981';
  label.textContent = _appStatusCurrent.toUpperCase();
}

function openAppStatusModal() {
  const modal = document.getElementById('appStatusModal');
  if (!modal) return;
  // Pre-select current state
  document.querySelectorAll('.app-status-opt').forEach(el => {
    el.classList.toggle('selected', el.dataset.status === _appStatusCurrent);
  });
  document.getElementById('appStatusMsgInput').value = _appStatusMessage || '';
  modal.classList.add('open');
  loadNavCompact();   // v134.7
  loadLangAr();   // v135.0
  loadThemeDark();   // v136.0
  loadLeaveReqSwitch();   // leave requests (decision 11)
  if (typeof ScanAdmin !== 'undefined' && ScanAdmin.loadSwitch) ScanAdmin.loadSwitch();   // v136.19: Scan in the staff app
  loadSecurityPush();   // v136.55
  loadAgentPushes();   // v136.67
}

// v134.7 - the shorter staff app bar (the 15 Oct 2026 look-and-feel review). Head Office's switch: app_config
// `nav_compact`, a boolean, OFF by default (a missing row is off). The staff app reads it tokenlessly through
// /api/config (PUBLIC_CONFIG_KEYS) at startApp; /admin/config is the write, already Head Office only on the Worker.
// null = not loaded yet or the read failed: then no switch is offered, so nobody saves from a made-up state.
var _navCompactOn = null;
function navCompactIsHeadOffice() {
  return typeof currentAdmin !== 'undefined' && !!currentAdmin && String(currentAdmin.role || '').toLowerCase() === 'admin';
}
async function loadNavCompact() {
  _navCompactOn = null;
  renderNavCompact('Loading...');
  try {
    const row = await DB.config.get('nav_compact');
    const v = row && typeof row === 'object' ? row.value : row;
    _navCompactOn = (v === true || v === 'true');
    renderNavCompact();
  } catch (e) {
    renderNavCompact('Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
  }
}
function renderNavCompact(msg) {
  const box = document.getElementById('navCompactBox');
  if (!box) return;
  if (msg || _navCompactOn === null) { box.textContent = msg || 'Loading...'; return; }
  const on = _navCompactOn;
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        // v136.32: computers and tills always have the short bar (a mouse cannot scroll the long one); this switch
        // now decides phones and tablets held upright (src/ui/compact-nav.js DESKTOP_QUERY)
        (on ? 'SHORT on every device: Today, Clock, Rota, Chat and More' : 'SHORT on computers and tills, FULL on phones') + '</span>' +
      (navCompactIsHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" id="navCompactBtn" onclick="setNavCompact(' + (on ? 'false' : 'true') + ')">' +
            (on ? 'Phones back to the full bar' : 'Shorter bar on phones too') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45"><span>The shorter bar keeps the four most used tabs; More opens every other tab the person can use, in groups. ' +
    'Saved straight away, separately from the status above. Each device changes the next time Shift starts or somebody signs in.</span> ' +
    '<span>Computers and tills always have the shorter bar, because a mouse cannot scroll the long one.</span></div>';
}
async function setNavCompact(on) {
  const btn = document.getElementById('navCompactBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await DB.config.set('nav_compact', !!on);
    _navCompactOn = !!on;
    showToast(on ? 'Staff app: the shorter bar is on.' : 'Staff app: back to the full bar.', 'success');
  } catch (e) {
    showToast('Could not change the staff app bar: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderNavCompact();
}

// v135.0 - the English/Arabic dropdown (src/i18n). Head Office's switch: app_config `lang_ar_enabled`, a boolean,
// OFF by default (a missing row is off); the apps read it tokenlessly through /api/config (PUBLIC_CONFIG_KEYS).
// Off also returns any device that had chosen Arabic to English. Same shape as the staff app bar switch above.
var _langArOn = null;
async function loadLangAr() {
  _langArOn = null;
  renderLangAr('Loading...');
  try {
    const row = await DB.config.get('lang_ar_enabled');
    const v = row && typeof row === 'object' ? row.value : row;
    _langArOn = (v === true || v === 'true');
    renderLangAr();
  } catch (e) {
    renderLangAr('Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
  }
}
function renderLangAr(msg) {
  const box = document.getElementById('langArBox');
  if (!box) return;
  if (msg || _langArOn === null) { box.textContent = msg || 'Loading...'; return; }
  const on = _langArOn;
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        (on ? 'ON: everyone can choose English or العربية' : 'OFF: English only') + '</span>' +
      (navCompactIsHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" id="langArBtn" onclick="setLangAr(' + (on ? 'false' : 'true') + ')">' +
            (on ? 'Turn the Arabic dropdown off' : 'Turn the Arabic dropdown on') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45">A dropdown on the sign-in screen and the top bar of the staff app and admin. Each device remembers its own choice. ' +
    'What people type (emails, chat, notes, names) stays as written. Each device changes the next time Shift opens.</div>';
}
// v136.0 - Head Office's switch for the Light / Dark / Auto button (app_config theme_dark_enabled; public key, read
// tokenlessly by src/theme). Same shape as the Arabic switch above.
var _themeDarkOn = null;
async function loadThemeDark() {
  _themeDarkOn = null;
  renderThemeDark('Loading...');
  try {
    const row = await DB.config.get('theme_dark_enabled');
    const v = row && typeof row === 'object' ? row.value : row;
    _themeDarkOn = (v === true || v === 'true');
    renderThemeDark();
  } catch (e) {
    renderThemeDark('Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
  }
}
function renderThemeDark(msg) {
  const box = document.getElementById('themeDarkBox');
  if (!box) return;
  if (msg || _themeDarkOn === null) { box.textContent = msg || 'Loading...'; return; }
  const on = _themeDarkOn;
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        (on ? 'ON: everyone can choose Light, Dark or Auto' : 'OFF: Light only') + '</span>' +
      (navCompactIsHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" id="themeDarkBtn" onclick="setThemeDark(' + (on ? 'false' : 'true') + ')">' +
            (on ? 'Turn the dark mode button off' : 'Turn the dark mode button on') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45">A button beside the language dropdown on the sign-in screen and the top bar of the staff app, admin and messages. ' +
    'Every device starts in Light and remembers its own choice; Auto follows the phone or till. Off returns every device to Light the next time Shift opens.</div>';
}
async function setThemeDark(on) {
  const btn = document.getElementById('themeDarkBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await DB.config.set('theme_dark_enabled', !!on);
    _themeDarkOn = !!on;
    showToast(on ? 'The dark mode button is on.' : 'The dark mode button is off: Light only.', 'success');
  } catch (e) {
    showToast('Could not change the dark mode setting: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderThemeDark();
}

// v136.55 - Head Office's switch for the weekly security watch's push (app_config security_watch_push). ON by default:
// a missing row is on (Matt, 26 Oct 2026: push for high and critical findings). The findings always land in the Dev tab.
var _secPushOn = null;
async function loadSecurityPush() {
  _secPushOn = null;
  renderSecurityPush('Loading...');
  try {
    const row = await DB.config.get('security_watch_push');
    const v = row && typeof row === 'object' ? row.value : row;
    _secPushOn = !(v === false || v === 'false');
    renderSecurityPush();
  } catch (e) {
    renderSecurityPush('Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
  }
}
function renderSecurityPush(msg) {
  const box = document.getElementById('securityPushBox');
  if (!box) return;
  if (msg || _secPushOn === null) { box.textContent = msg || 'Loading...'; return; }
  const on = _secPushOn;
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        (on ? 'ON: Head Office gets a push for high and critical findings' : 'OFF: findings go to the Dev tab only') + '</span>' +
      (navCompactIsHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" id="securityPushBtn" onclick="setSecurityPush(' + (on ? 'false' : 'true') + ')">' +
            (on ? 'Turn the security push off' : 'Turn the security push on') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45">Every Monday the security watch checks the code libraries and the services Shift runs on for newly published security problems. ' +
    'Anything that affects Shift is filed in the Dev tab as a report from the Security agent, whatever this switch says.</div>';
}
// v136.67 - the same switch for the two agents that now file the way the security watch does (src/worker/findings-
// inbox.js): the night watch (Jack's 3am sweep) and the weekly pay war game. ON by default; the findings always go
// to the Dev tab, the switch only decides the push for high and critical ones.
const AGENT_PUSHES = [
  { key: 'night_watch_push', box: 'nightWatchPushBox', name: 'night watch',
    what: 'Every night at 3am Jack checks clocking, rotas, data feeds, the integrity checks and the backlog. Each problem he finds is filed once in the Dev tab as a report from the Night watch, and reopened if it comes back after it was marked done.' },
  { key: 'pay_watch_push', box: 'payWatchPushBox', name: 'pay war game',
    what: 'Every Friday Claude reads the pay code (clocking, the 3am day, TOIL, balances, Qatar weeks, the Amanah feed) looking for ways someone could be paid wrong. Each finding is filed once in the Dev tab as a report from the Pay agent, to check before fixing.' },
];
const _agentPushOn = {};
async function loadAgentPushes() {
  for (const a of AGENT_PUSHES) {
    _agentPushOn[a.key] = null;
    renderAgentPush(a.key, 'Loading...');
    try {
      const row = await DB.config.get(a.key);
      const v = row && typeof row === 'object' ? row.value : row;
      _agentPushOn[a.key] = !(v === false || v === 'false');
      renderAgentPush(a.key);
    } catch (e) {
      renderAgentPush(a.key, 'Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
    }
  }
}
function renderAgentPush(key, msg) {
  const a = AGENT_PUSHES.find((x) => x.key === key);
  const box = a && document.getElementById(a.box);
  if (!box) return;
  const on = _agentPushOn[key];
  if (msg || on === null || on === undefined) { box.textContent = msg || 'Loading...'; return; }
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        (on ? 'ON: Head Office gets a push for high and critical findings' : 'OFF: findings go to the Dev tab only') + '</span>' +
      (navCompactIsHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" onclick="setAgentPush(\'' + key + '\',' + (on ? 'false' : 'true') + ')">' +
            (on ? 'Turn the ' + a.name + ' push off' : 'Turn the ' + a.name + ' push on') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45">' + a.what + '</div>';
}
async function setAgentPush(key, on) {
  const a = AGENT_PUSHES.find((x) => x.key === key);
  if (!a) return;
  try {
    await DB.config.set(key, !!on);
    _agentPushOn[key] = !!on;
    showToast(on ? 'The ' + a.name + ' push is on.' : 'The ' + a.name + ' push is off: findings go to the Dev tab only.', 'success');
  } catch (e) {
    showToast('Could not change the ' + a.name + ' push: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderAgentPush(key);
}

async function setSecurityPush(on) {
  const btn = document.getElementById('securityPushBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await DB.config.set('security_watch_push', !!on);
    _secPushOn = !!on;
    showToast(on ? 'The security push is on.' : 'The security push is off: findings go to the Dev tab only.', 'success');
  } catch (e) {
    showToast('Could not change the security push: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderSecurityPush();
}

// Leave requests (docs/leave-requests-plan.md, decision 11): Head Office's switch, app_config leave_requests_enabled, a
// boolean, OFF by default (a missing row is off). The Worker reads it on every leave request route (staging is always
// on there), so the till and the admin page follow it without a reload. Same shape as the switches above.
var _leaveReqOn = null;
async function loadLeaveReqSwitch() {
  _leaveReqOn = null;
  renderLeaveReqSwitch('Loading...');
  try {
    const row = await DB.config.get('leave_requests_enabled');
    const v = row && typeof row === 'object' ? row.value : row;
    _leaveReqOn = (v === true || v === 'true');
    renderLeaveReqSwitch();
  } catch (e) {
    renderLeaveReqSwitch('Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
  }
}
function renderLeaveReqSwitch(msg) {
  const box = document.getElementById('leaveReqSwitchBox');
  if (!box) return;
  if (msg || _leaveReqOn === null) { box.textContent = msg || 'Loading...'; return; }
  const on = _leaveReqOn;
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        (on ? 'ON: staff can ask for leave on the till' : 'OFF: no leave requests') + '</span>' +
      (navCompactIsHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" id="leaveReqSwitchBtn" onclick="setLeaveReqSwitch(' + (on ? 'false' : 'true') + ')">' +
            (on ? 'Turn leave requests off' : 'Turn leave requests on') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45">Request leave on the till (Balances) and People, Leave requests in admin. ' +
    'Off hides both; leave already approved stays on the rota. Staging always has it on, for checking.</div>';
}
async function setLeaveReqSwitch(on) {
  const btn = document.getElementById('leaveReqSwitchBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await DB.config.set('leave_requests_enabled', !!on);
    _leaveReqOn = !!on;
    showToast(on ? 'Leave requests are on.' : 'Leave requests are off.', 'success');
  } catch (e) {
    showToast('Could not change the leave requests setting: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderLeaveReqSwitch();
}

async function setLangAr(on) {
  const btn = document.getElementById('langArBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await DB.config.set('lang_ar_enabled', !!on);
    _langArOn = !!on;
    showToast(on ? 'The Arabic dropdown is on.' : 'The Arabic dropdown is off: English only.', 'success');
  } catch (e) {
    showToast('Could not change the Arabic setting: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderLangAr();
}

function closeAppStatusModal() {
  document.getElementById('appStatusModal').classList.remove('open');
}

function selectAppStatusOption(status) {
  document.querySelectorAll('.app-status-opt').forEach(el => {
    el.classList.toggle('selected', el.dataset.status === status);
  });
  // Store intent on the modal dataset
  document.getElementById('appStatusModal').dataset.pendingStatus = status;
}

async function saveAppStatus() {
  const modal = document.getElementById('appStatusModal');
  const status = modal.dataset.pendingStatus
              || (document.querySelector('.app-status-opt.selected') || {}).dataset?.status
              || _appStatusCurrent;
  const message = (document.getElementById('appStatusMsgInput').value || '').trim() || null;

  // Friendly confirm if going to red — it's disruptive
  if (status === 'red' && _appStatusCurrent !== 'red') {
    if (!confirm('Set app to RED? This will block all staff devices — they will be forced to use paper until you change it back.\n\nContinue?')) {
      return;
    }
  }

  const btn = document.getElementById('appStatusSaveBtn');
  const prevText = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    await DB.appStatus.set({
      status,
      message,
      updated_by: (currentAdmin && currentAdmin.name) || 'admin',
    });
    _appStatusCurrent = status;
    _appStatusMessage = message;
    renderAppStatusPill();
    closeAppStatusModal();
    showToast('App status set to ' + status.toUpperCase() + (message ? ' — "' + message + '"' : ''), 'success');
  } catch(e) {
    console.error('[AppStatus] save failed:', e);
    showToast('⚠️ Failed to update app status: ' + (e && e.message ? e.message : 'unknown'), 'error');
  } finally {
    // v135.2: put back the English label, never the text read off the screen: in Arabic that is Arabic, and it would
    // stay Arabic after switching back, because the translator only restores text it swapped itself
    if (btn) { btn.disabled = false; btn.textContent = prevText && !/[؀-ۿ]/.test(prevText) ? prevText : 'Save'; }
  }
}

// Load status on admin page load
if (typeof currentAdmin !== 'undefined') {
  // If the auth check has already populated currentAdmin, load now
  loadAppStatus();
} else {
  // Otherwise wait briefly for it
  setTimeout(loadAppStatus, 1000);
}

// Path-to-8 Stage 5 (v80.11): the Top 50 admin module (~360 lines that
// previously lived here at the end of admin.html — upload, parse, preview,
// commit, history, delete) is now in src/top50/admin.js, bundled into
// dist/top50.js by Netlify on each deploy. The bundle is loaded as a
// <script src="dist/top50.js"> tag in <head>; this small init call wires
// up the file-picker drop zone for the upload UI.
Top50.admin.wireDropZone();

function setAreaKpiScope(scope) {
  _areaKpiScope = scope;
  // v131.14: a scope button click leaves month mode — clear the picker so the
  // UI can't show a month while the data shows MTD.
  _areaKpiMonth = null;
  const mp = document.getElementById('areaKpiMonthPick');
  if (mp) mp.value = '';
  // Update tab highlighting
  document.querySelectorAll('#areaKpiScopeTabs .btn').forEach(b => {
    if (b.getAttribute('data-scope') === scope) {
      b.classList.remove('btn-outline'); b.classList.add('btn-green');
    } else {
      b.classList.remove('btn-green'); b.classList.add('btn-outline');
    }
  });
  Uploads.kpi.renderAreaKpis();
}

// v103.0: renderAreaKpis → Uploads.kpi.renderAreaKpis
// Color helper — same thresholds as the staff KPI screen.
// v103.0: _kpiPctColor → Uploads.kpi._kpiPctColor
// v103.0: _kpiFmtPct → Uploads.kpi._kpiFmtPct
// v103.0: _kpiFmtNum → Uploads.kpi._kpiFmtNum
// v103.0: _kpiFmtCur → Uploads.kpi._kpiFmtCur
// v103.0: drawAreaKpiTable → Uploads.kpi.drawAreaKpiTable
function toggleAreaKpiExpand(areaId) {
  console.log('[areaKpis] toggleAreaKpiExpand called with', areaId);
  // Defensive: data attribute coercion or any odd JSON parsing could give
  // us a string. Set comparison is strict so coerce to number first.
  areaId = Number(areaId);
  if (!isFinite(areaId)) { console.warn('[areaKpis] bad areaId, ignoring'); return; }
  if (_areaKpiExpanded.has(areaId)) _areaKpiExpanded.delete(areaId);
  else _areaKpiExpanded.add(areaId);
  Uploads.kpi.drawAreaKpiTable();
}
// Belt-and-braces: explicitly expose the handlers on window so inline
// onclick="toggleAreaKpiExpand(...)" attributes always find them, even if
// some unusual script-loading context (CSP, build pipeline, or some other
// quirk) prevents top-level function declarations from auto-hoisting onto
// the global object. Cheap, no downside.
if (typeof window !== 'undefined') {
  window.toggleAreaKpiExpand = toggleAreaKpiExpand;
  window.setAreaKpiScope     = setAreaKpiScope;
  window.renderAreaKpis      = Uploads.kpi.renderAreaKpis;
}

window.renderDashboardAreaStats = renderDashboardAreaStats;
window.renderDashboard = renderDashboard;
window.renderDashboardCustomerSat = renderDashboardCustomerSat;
window.renderDashboardBrief = renderDashboardBrief;   // v134.5: the card's Try again button
window.custSatTile = custSatTile;
window.summarizeHoursRow = summarizeHoursRow;
window.switchHoursSeason = switchHoursSeason;
window.loadCurrentSeason = loadCurrentSeason;
window.updateCurrentSeasonBadge = updateCurrentSeasonBadge;
window.toggleCurrentSeason = toggleCurrentSeason;
window.copyHoursDayToRange = copyHoursDayToRange;
window.safeTimeInputValue = safeTimeInputValue;
window.downloadClockLogCSV = downloadClockLogCSV;
window.runClockLogAI = runClockLogAI;
window.updateStoreAreaOptions = updateStoreAreaOptions;
window.openAddStoreModal = openAddStoreModal;
window.openEditStoreModal = openEditStoreModal;
window.changeStorePassword = changeStorePassword;
window.unlockStoreLogin = unlockStoreLogin;   // v136.9
window.buildDayTogglesIn = buildDayTogglesIn;
window.addShiftTemplateToForm = addShiftTemplateToForm;
window.populateWriteAuditStoreFilter = populateWriteAuditStoreFilter;
window.resetAllConsent = resetAllConsent;
window.initDevAuditFilters = initDevAuditFilters;
window.updateErrorBadge = updateErrorBadge;
window.setErrorStatus = setErrorStatus;
window.loadAppStatus = loadAppStatus;
window.renderAppStatusPill = renderAppStatusPill;
window.openAppStatusModal = openAppStatusModal;
window.closeAppStatusModal = closeAppStatusModal;
window.selectAppStatusOption = selectAppStatusOption;
window.saveAppStatus = saveAppStatus;
window.setNavCompact = setNavCompact;   // v134.7
window.setLangAr = setLangAr;   // v135.0
window.setThemeDark = setThemeDark;   // v136.0
window.setSecurityPush = setSecurityPush;   // v136.55
window.setAgentPush = setAgentPush;   // v136.67
window.setLeaveReqSwitch = setLeaveReqSwitch;   // leave requests (decision 11)
export { loadNavCompact, renderNavCompact, setNavCompact, loadLangAr, renderLangAr, setLangAr, loadThemeDark, renderThemeDark, setThemeDark, loadLeaveReqSwitch, renderLeaveReqSwitch, setLeaveReqSwitch };   // v134.7 (exported too: the globalName clobber rule)
export { loadSecurityPush, renderSecurityPush, setSecurityPush };   // v136.55
export { loadAgentPushes, renderAgentPush, setAgentPush, AGENT_PUSHES };   // v136.67
window.setAreaKpiScope = setAreaKpiScope;
window.toggleAreaKpiExpand = toggleAreaKpiExpand;

// ── Store location (v131.72) ─────────────────────────────────────────────────
//
// Matt asked for the survey geotag. The question everyone skips is where the
// store coordinates come from: a geocoding API needs a billed key, a desk
// lookup is slow, and an agent typing in numbers it believes are right is the
// worst option of the three — a wrong coordinate silently files a review
// against the wrong shop, which is worse than no geotag at all.
//
// So they are captured BY SOMEBODY STANDING IN THE SHOP. Right by construction,
// free, and an area manager can do a whole area on one round.
//
// The field is one text box holding "lat, lng" rather than two number inputs,
// because that is the format Google Maps puts on the clipboard when you
// right-click a spot — so the paste path works with no reformatting.

export function captureStoreLocation() {
  const box = document.getElementById('smStoreCoords');
  const btn = document.getElementById('smStoreCoordsBtn');
  if (!box) return;
  if (!navigator.geolocation) { showToast('This device cannot report its location', 'error'); return; }
  if (btn) { btn.disabled = true; btn.textContent = 'Getting location…'; }
  const done = () => { if (btn) { btn.disabled = false; btn.innerHTML = '&#128205; Use my current location'; } };
  navigator.geolocation.getCurrentPosition((pos) => {
    done();
    box.value = formatCoords(pos.coords.latitude, pos.coords.longitude);
    // Accuracy is worth showing: a 2km fix (wifi positioning indoors, which
    // malls are full of) looks identical to a 5m one in the box otherwise, and
    // the whole point of capturing on site is that it is accurate.
    const acc = Math.round(pos.coords.accuracy || 0);
    showToast(acc > 100
      ? 'Captured, but only accurate to about ' + acc + 'm — try again near a window or outside the shop'
      : 'Location captured (accurate to about ' + acc + 'm)', acc > 100 ? 'error' : 'success');
  }, (e) => {
    done();
    showToast(e && e.code === 1
      ? 'Location permission was refused — allow it for this site, or paste the coordinates from Google Maps'
      : 'Could not get a location fix. Try again, or paste the coordinates from Google Maps', 'error');
  }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
}
